import { NavLink, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface MenuItem {
  icon: string;
  label: string;
  path: string;
  badge?: number;
}

const menuItems: MenuItem[] = [
  { icon: 'ri-dashboard-line', label: 'Dashboard', path: '/admin/dashboard' },
  { icon: 'ri-file-list-3-line', label: 'Başvurular', path: '/admin/basvurular', badge: 8 },
  { icon: 'ri-graduation-cap-line', label: 'Öğrenciler', path: '/admin/ogrenciler' },
  { icon: 'ri-user-star-line', label: 'Öğretmenler', path: '/admin/ogretmenler' },
  { icon: 'ri-send-plane-line', label: 'Öğrenci Yönlendirme', path: '/admin/yonlendirme' },
  { icon: 'ri-calendar-check-line', label: 'Deneme Dersleri', path: '/admin/deneme-dersleri' },
  { icon: 'ri-book-open-line', label: 'Aktif Dersler', path: '/admin/aktif-dersler' },
  { icon: 'ri-money-dollar-circle-line', label: 'Ödeme & Dekontlar', path: '/admin/odemeler', badge: 3 },
  { icon: 'ri-coins-line', label: 'Öğretmen Hak Edişleri', path: '/admin/hak-edisler' },
  { icon: 'ri-group-line', label: 'Grup Dersleri', path: '/admin/grup-dersleri' },
  { icon: 'ri-bar-chart-2-line', label: 'Raporlar', path: '/admin/raporlar' },
  { icon: 'ri-settings-3-line', label: 'Ayarlar', path: '/admin/ayarlar' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const { profile, logout } = useAuth();

  const displayName = profile?.ad_soyad ?? 'Yönetici';

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/');
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={onClose} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full z-40 w-64 flex flex-col transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ background: 'linear-gradient(180deg, #0f1f3d 0%, #1a2f5a 60%, #162548 100%)' }}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 py-5 border-b border-white/10">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/10 border border-white/15 flex-shrink-0">
            <i className="ri-book-open-line text-white text-base" />
          </div>
          <div>
            <p className="text-white font-bold text-base leading-tight tracking-wide">DERSEGEL</p>
            <p className="text-white/40 text-xs">Antalya Yönetim Paneli</p>
          </div>
          <button className="ml-auto lg:hidden w-7 h-7 flex items-center justify-center text-white/60 hover:text-white cursor-pointer" onClick={onClose}>
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3">
          <p className="text-white/30 text-xs font-semibold uppercase tracking-widest px-3 mb-3">Yönetim</p>
          <ul className="space-y-0.5">
            {menuItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer group ${isActive ? 'bg-white/15 text-white border border-white/10' : 'text-white/60 hover:text-white hover:bg-white/8'}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${isActive ? 'text-[#7ab3f5]' : 'text-white/50 group-hover:text-white/80'}`}>
                        <i className={`${item.icon} text-base`} />
                      </div>
                      <span className="flex-1 text-sm">{item.label}</span>
                      {item.badge && (
                        <span className="bg-[#ef4444] text-white text-xs px-1.5 py-0.5 rounded-full font-bold leading-none">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User info + logout */}
        <div className="px-3 pb-5 border-t border-white/10 pt-4">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/8 mb-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#3b5bdb] flex-shrink-0">
              <span className="text-white text-sm font-bold">{displayName.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-xs font-semibold truncate">{displayName}</p>
              <p className="text-white/40 text-xs truncate">Yönetici</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            disabled={loggingOut}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-white/50 hover:text-white hover:bg-white/8 w-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              {loggingOut ? <i className="ri-loader-4-line animate-spin text-base" /> : <i className="ri-logout-box-line text-base" />}
            </div>
            {loggingOut ? 'Çıkış yapılıyor...' : 'Çıkış Yap'}
          </button>
        </div>
      </aside>
    </>
  );
}